import dayjs from 'dayjs';

import { calculateAging } from 'utils';
import {
  COMMENT_SOURCE,
  LOST_ORDER_SLA_MAX_LEVEL
} from 'constants/lost-or-damaged';
import aclMatrix from 'common/aclMatrix';
import { ACL_MATRIX } from 'common/permissions';
import { isUserAuthorized } from 'utils/helpers';
import { fmt } from 'IntlWrapper/IntlWrapper';

import { ProofModalTrigger } from './components/ProofModal/ProofModalTrigger/ProofModalTrigger';
import { PendingOnFleetActions } from './components/Actions/PendingOnFleetActions';
import { PendingOnAuditHubActions } from './components/Actions/PendingOnAuditHubActions';
import { PendingOnAuditFleetActions } from './components/Actions/PendingOnAuditFleetActions';
import { PendingOnUnknownActions } from './components/Actions/PendingOnUnknownActions';
import { ProductDetailsModalTrigger } from './components/ProductDetailsModalTrigger/ProductDetailsModalTrigger';
import { TrackingNumberCell } from './components/TrackingNumberCell/TrackingNumberCell';
import EscalationSeverity from 'components/Escalation/components/TicketsAging/components/EscalationSeverity/EscalationSeverity';

export const formatPendingOnHubData = (data) => {
  const formattedData = data.map((delivery) => {
    const hubName = delivery?.state?.deliveryLossLiability?.liableHubName;
    const flaggedBy = delivery?.state?.deliveryLossLiability?.flaggedBy?.name;
    const hubId = delivery?.state?.deliveryLossLiability?.liableHubId;
    const slaAging =
      calculateAging(delivery?.state?.deliveryLossLiability?.time) + 1;

    return {
      order_id: <TrackingNumberCell trackingNumber={delivery.trackingNumber} />,
      _id: delivery?._id,
      liableHubId: hubId,
      current_hub: hubName,
      flagged_by: flaggedBy,
      sla: (
        <EscalationSeverity
          barClassName={'br-escalation-severity__lost-orders'}
          severityLevel={slaAging >= 2 ? 2 : slaAging}
          severityMaxLevel={LOST_ORDER_SLA_MAX_LEVEL}
        />
      )
    };
  });
  return formattedData;
};

export const formatPendingOnFleetData = (data) => {
  const formattedData = data.map((delivery) => {
    const hubName = delivery?.state?.receivedAtWarehouse?.warehouse?.name;
    const starName = delivery?.state?.deliveryLossLiability?.liableStarName;
    const starPhoneNumber =
      delivery?.state?.deliveryLossLiability?.liableStarPhone;
    const hubId = delivery?.state?.deliveryLossLiability?.liableHubId;
    const timeLeft = delivery?.remainingLostSLA;

    return {
      orderId: (
        <TrackingNumberCell
          trackingNumber={delivery.trackingNumber}
          timeLeft={timeLeft}
        />
      ),
      _id: delivery?._id,
      currentHub: hubName,
      starName: (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <span>{starName}</span>
          <span className="caption_sm br-lost-orders__star-phone-number">
            {starPhoneNumber}
          </span>
        </div>
      ),
      proof: <ProofModalTrigger delivery={delivery} />,
      actions: <PendingOnFleetActions hubId={hubId} delivery={delivery} />
    };
  });

  return formattedData;
};

export const formatPendingOnAuditHubData = (data) => {
  const formattedData = data.map((delivery) => {
    const timeLeft = delivery?.remainingLostSLA;
    return {
      orderId: (
        <TrackingNumberCell
          trackingNumber={delivery?.trackingNumber}
          timeLeft={timeLeft}
        />
      ),
      currentHub: delivery?.state?.receivedAtWarehouse?.warehouse?.name,
      flaggedBy: delivery?.state?.deliveryLossLiability?.flaggedBy?.name,
      proof: <ProofModalTrigger delivery={delivery} fromHub />,
      actions: <PendingOnAuditHubActions delivery={delivery} />
    };
  });

  return formattedData;
};

export const formatPendingOnAuditFleetData = (data) => {
  const formattedData = data.map((delivery) => {
    const hubName = delivery?.state?.receivedAtWarehouse?.warehouse?.name;
    const starName = delivery?.state?.deliveryLossLiability?.liableStarName;
    const starPhoneNumber =
      delivery?.state?.deliveryLossLiability?.liableStarPhone;
    const timeLeft = delivery?.remainingLostSLA;

    return {
      orderId: (
        <TrackingNumberCell
          trackingNumber={delivery.trackingNumber}
          timeLeft={timeLeft}
        />
      ),
      currentHub: hubName,
      starName: (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <span>{starName}</span>
          <span className="caption_sm br-lost-orders__star-phone-number">
            {starPhoneNumber}
          </span>
        </div>
      ),
      hubProof: <ProofModalTrigger delivery={delivery} fromFleet />,

      fleetJustification: (
        <span className="caption br-lost-orders__fleet-justification">
          {
            delivery?.state?.deliveryLossLiability?.comments?.find(
              (comment) => comment?.source === COMMENT_SOURCE.STAR
            )?.text
          }
        </span>
      ),
      actions: <PendingOnAuditFleetActions delivery={delivery} />
    };
  });

  return formattedData;
};

export const formatPendingOnUnknownData = (data, allHubs) => {
  const formattedData = data.map((delivery) => {
    const flaggedBy = delivery?.state?.deliveryLossLiability?.flaggedBy?.name;
    const role = getRoleString(
      delivery?.state?.deliveryLossLiability?.flaggedBy?.role
    );
    const hubName = delivery?.state?.deliveryLossLiability?.liableHubName;
    const flaggedByHubName =
      delivery?.state?.deliveryLossLiability?.flaggedBy?.warehouse?.name;
    const description = delivery?.specs?.packageDetails?.description;
    const timeLeft = delivery?.remainingLostSLA;

    return {
      _id: delivery?._id,
      orderId: (
        <TrackingNumberCell
          trackingNumber={delivery?.trackingNumber}
          timeLeft={timeLeft}
        />
      ),
      currentHub: hubName,
      flaggedBy: (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <span>{flaggedBy}</span>
          <span className="caption_sm br-lost-orders__star-phone-number">
            {role} {flaggedByHubName}
          </span>
        </div>
      ),
      description: (
        <span className="caption_sm br-lost-orders__star-phone-number">
          {description || 'N/A'}
        </span>
      ),
      image:
        !delivery?.hasProductInfo && !delivery?.extraData ? (
          'No image'
        ) : (
          <ProductDetailsModalTrigger
            delivery={delivery}
            buttonLabel={fmt({
              id: 'ops_control.lost_orders.proof_modal.view_image'
            })}
          />
        ),
      actions: <PendingOnUnknownActions delivery={delivery} />
    };
  });

  return formattedData;
};

export const formatConfirmedLostOrdersData = (data) => {
  const formattedData = data
    .sort((a, b) => {
      return (
        new Date(b?.state?.deliveryLossLiability?.confirmedLost?.time) -
        new Date(a?.state?.deliveryLossLiability?.confirmedLost?.time)
      );
    })
    .map((delivery) => {
      return {
        orderId: (
          <TrackingNumberCell trackingNumber={delivery.trackingNumber} />
        ),
        currentHub: delivery?.state?.deliveryLossLiability?.liableHubName,
        flaggedBy: delivery?.state?.deliveryLossLiability?.flaggedBy?.name,
        markedLost: dayjs(
          delivery?.state?.deliveryLossLiability?.confirmedLost?.time
        ).format('DD/MM/YYYY'),
        liableOn: delivery?.state?.deliveryLossLiability?.liabilityOn
      };
    });

  return formattedData;
};

export const getInitialQuickFilter = () => {
  if (
    isUserAuthorized(aclMatrix.HUBS, [
      ACL_MATRIX.HUBS_ALLOWED_TO_GET_OPS_ROOM_LOST_DATA_PENDING_ON_HUB
    ])
  ) {
    return '0';
  }
  if (
    isUserAuthorized(aclMatrix.HUBS, [
      ACL_MATRIX.HUBS_ALLOWED_TO_GET_OPS_ROOM_LOST_DATA_PENDING_ON_FLEET
    ])
  ) {
    return '1';
  }
  if (
    isUserAuthorized(aclMatrix.HUBS, [
      ACL_MATRIX.HUBS_ALLOWED_TO_GET_OPS_ROOM_LOST_DATA_PENDING_ON_AUDIT
    ])
  ) {
    return '2';
  }
  if (
    isUserAuthorized(aclMatrix.HUBS, [
      ACL_MATRIX.HUBS_ALLOWED_TO_GET_OPS_ROOM_LOST_DATA_PENDING_ON_UNKNOWN
    ])
  ) {
    return '3';
  }
  return '4';
};

const getRoleString = (role) => {
  return role
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

export const calculateRemainingSLATimeString = (timeLeft) => {
  if (!timeLeft) return { timeString: '', isUrgent: false };

  const [_, hours, minutes] = timeLeft.split(':').map(Number);
  const isUrgent = hours < 4;

  if (hours > 0) {
    return {
      timeString: fmt(
        { id: 'ops_control.lost_orders.time_left.hours_left' },
        { hours }
      ),
      isUrgent
    };
  }

  if (minutes > 0) {
    return {
      timeString: fmt(
        { id: 'ops_control.lost_orders.time_left.minutes_left' },
        { minutes }
      ),
      isUrgent
    };
  }

  // return empty string if time left is 00:00:00
  return { timeString: '', isUrgent: false };
};
